"use client"

import { X, Users, User, Crown, Calendar } from "lucide-react"
import { generatePresignedUrl } from "../postFileAws"
import { useState, useEffect } from "react"

const GroupInfoModal = ({ isOpen, onClose, groupInfo, profileImageUrls, onParticipantClick, currentUserId }) => {
  const [participantImages, setParticipantImages] = useState({})
  const [isLoadingImages, setIsLoadingImages] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

const filteredParticipants = groupInfo?.participants?.filter((participant) =>
  participant.pName.toLowerCase().includes(searchTerm.toLowerCase())
) || []

  useEffect(() => {
    if (groupInfo?.participants && isOpen) {
      loadParticipantImages()
    }
  }, [groupInfo, isOpen])

  const loadParticipantImages = async () => {
    if (!groupInfo?.participants) return

    setIsLoadingImages(true)
    const imageUrls = { ...participantImages }
    let hasNewImages = false

    for (const participant of groupInfo.participants) {
      if (participant.profilePhotoS3Uri && !imageUrls[participant.pid] && !profileImageUrls[participant.pid]) {
        try {
          const presignedUrl = await generatePresignedUrl(participant.profilePhotoS3Uri)
          imageUrls[participant.pid] = presignedUrl
          hasNewImages = true
        } catch (error) {
          console.error("Error loading participant image:", error)
        }
      }
    }

    if (hasNewImages) {
      setParticipantImages(imageUrls)
    }
    setIsLoadingImages(false)
  }

  const getParticipantImage = (participant) => {
    return profileImageUrls[participant.pid] || participantImages[participant.pid]
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      })
    } catch {
      return "Unknown"
    }
  }

  const isGroupAdmin = (participant) => {
    return groupInfo?.participants?.[0]?.pid === participant.pid
  }

  if (!isOpen || !groupInfo) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 overflow-hidden max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Group Info</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Created Date */}
          <div className="flex items-center mb-6">
            <Calendar className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-sm text-gray-700">Created: {formatDate(groupInfo?.createdAt)}</p>
          </div>
<div className="mb-4">
  <input
    type="text"
    placeholder="Search participants..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
</div>

          {/* Participants */}
          <h4 className="text-md font-semibold text-gray-800 mb-2">
            Participants ({groupInfo?.participants?.length || 0})
          </h4>
          <div className="space-y-3">
          {filteredParticipants.map((participant) => (

              <div
                key={participant.pid}
                onClick={() => {
                  onParticipantClick(participant)
                  onClose()
                }}
                className="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer"
              >
                <div className="relative mr-3">
                  {getParticipantImage(participant) ? (
                    <img
                      src={getParticipantImage(participant)}
                      alt={participant.pName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                  {isGroupAdmin(participant) && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Crown className="h-2.5 w-2.5 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">
                    {participant.pName}
                    {participant.pid === currentUserId && <span className="text-xs text-blue-500 ml-1">(You)</span>}
                  </p>
                  {isGroupAdmin(participant) && (
                    <span className="text-xs text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full mt-1 inline-block">
                      Admin
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isLoadingImages && (
            <div className="flex justify-center py-4">
              <div className="animate-spin h-5 w-5 border-b-2 border-blue-500 rounded-full"></div>
              <span className="ml-2 text-sm text-gray-500">Loading images...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GroupInfoModal
